// =============================================================================
// VandieZ admin-web -- Trip detail (/trips/:tripId)
// =============================================================================
// Full triage view backed by GET /admin/trips/{id}.  Single payload,
// no follow-up calls — joins through load + parties + vehicle + stops
// + incidents + status history are all server-side.
//
// Layout: header + four sections stacked top-to-bottom so an operator
// reading the page top-to-bottom gets identity → money → progress →
// audit narrative without scrolling laterally.
//   1. Header card: lane, status pill, key counters (replacement / sub).
//   2. Parties + vehicle: shipper, driver, owner, vehicle reg.  Each is
//      clickable to the corresponding /users/{id} or /vehicles/{id}.
//   3. Money: trip value, applied commission rate, collection status,
//      cancellation fee if any.
//   4. Stops (multi-stop trips), Incidents, Status history — three
//      collapsible cards.  Empty arrays render a "none" placeholder.
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  Ban,
  MapPin,
  Receipt,
  RefreshCw,
  Truck,
  UserCog,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { tripsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { useToast } from "@/lib/toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  AdminTripDetail,
  AdminTripIncidentRow,
  AdminTripParty,
  AdminTripStatusHistoryRow,
  AdminTripStopRow,
  AdminTripTimelineEntry,
  AdminTripTimelineResponse,
  AdminTripVehicleSlice,
} from "@/types/admin_trips";

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function laneLabel(t: AdminTripDetail): string {
  const pickup = t.pickup_district ?? "—";
  const drop = t.drop_district ?? "—";
  return `${pickup} → ${drop}`;
}

const STATUS_PILL: Record<string, string> = {
  booked: "bg-muted text-muted-foreground",
  en_route_pickup: "bg-success-subtle text-success-subtle-foreground",
  arrived_pickup: "bg-primary-subtle text-primary-subtle-foreground",
  loading: "bg-warning-subtle text-warning-subtle-foreground",
  in_transit: "bg-success-subtle text-success-subtle-foreground",
  arrived_drop: "bg-primary-subtle text-primary-subtle-foreground",
  unloading: "bg-warning-subtle text-warning-subtle-foreground",
  // Lighter success wash than the in-flight statuses above -- mirrors the
  // original emerald-50-vs-emerald-100 split so a finished trip stays
  // distinguishable from one still moving.
  completed: "bg-success-subtle/60 text-success-subtle-foreground",
  cancelled: "bg-danger-subtle text-danger-subtle-foreground",
  disputed: "bg-info-subtle text-info-subtle-foreground",
};

function statusPillClass(status: string): string {
  return STATUS_PILL[status] ?? "bg-muted text-muted-foreground";
}

function PartyChip({
  label,
  party,
}: {
  label: string;
  party: AdminTripParty | null;
}): React.JSX.Element {
  if (party === null) {
    return (
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="text-muted-foreground">—</div>
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <Link
        to={`/users/${party.id}`}
        className="font-medium hover:underline"
      >
        {party.full_name ?? party.id.slice(0, 8)}
      </Link>
      <div className="text-xs text-muted-foreground font-mono">
        {party.phone ?? party.id.slice(0, 8)}
      </div>
    </div>
  );
}

function VehicleChip({
  vehicle,
}: {
  vehicle: AdminTripVehicleSlice;
}): React.JSX.Element {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        Vehicle
      </div>
      <Link
        to={`/vehicles/${vehicle.id}`}
        className="font-medium hover:underline"
      >
        {vehicle.registration_number ?? vehicle.id.slice(0, 8)}
      </Link>
      <div className="text-xs text-muted-foreground">
        {vehicle.vehicle_type ?? "—"}
        {vehicle.payload_capacity_kg !== null && (
          <> · {vehicle.payload_capacity_kg} kg</>
        )}
      </div>
    </div>
  );
}

function StopRow({ stop }: { stop: AdminTripStopRow }): React.JSX.Element {
  const arrived = stop.arrived_at !== null;
  const completed = stop.completed_at !== null;
  return (
    <li className="border-l-2 pl-3 py-1.5 ml-1 relative">
      <span
        className={cn(
          "absolute -left-[6px] top-2 h-3 w-3 rounded-full border-2 border-background",
          completed
            ? "bg-success"
            : arrived
              ? "bg-warning"
              // Not-yet-reached stop. --muted is a near-white surface tone and
              // vanished against the card, so this uses a tinted foreground
              // instead to keep the dot visible (it replaced slate-300).
              : "bg-muted-foreground/40",
        )}
        aria-hidden
      />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium">
            #{stop.sequence} {stop.stop_type ?? ""} · {stop.district ?? "—"}
          </div>
          <div className="text-xs text-muted-foreground">
            {stop.address ?? "—"}
          </div>
          {stop.contact_name !== null && (
            <div className="text-xs text-muted-foreground">
              {stop.contact_name}
              {stop.contact_phone !== null && (
                <span className="font-mono ml-1">({stop.contact_phone})</span>
              )}
            </div>
          )}
        </div>
        <div className="text-xs text-right shrink-0">
          <div>
            <span className="text-muted-foreground">Arrived: </span>
            {formatDateTime(stop.arrived_at)}
          </div>
          <div>
            <span className="text-muted-foreground">Completed: </span>
            {formatDateTime(stop.completed_at)}
          </div>
          {stop.progress_status !== null && (
            <div className="text-muted-foreground capitalize">
              {stop.progress_status.replace(/_/g, " ")}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function IncidentRow({
  incident,
}: {
  incident: AdminTripIncidentRow;
}): React.JSX.Element {
  return (
    <li className="border rounded-md p-3 text-sm space-y-1">
      <div className="flex items-center justify-between">
        <span className="font-medium capitalize">
          {incident.kind.replace(/_/g, " ")}
        </span>
        <span className="text-xs text-muted-foreground">
          {formatDateTime(incident.reported_at)}
        </span>
      </div>
      {incident.notes !== null && (
        <div className="text-muted-foreground">{incident.notes}</div>
      )}
      {(incident.old_vehicle_id !== null ||
        incident.new_vehicle_id !== null) && (
        <div className="text-xs text-muted-foreground">
          <span className="uppercase tracking-wide">Vehicle:</span>{" "}
          <span className="font-medium">
            {incident.old_vehicle_registration ??
              incident.old_vehicle_id?.slice(0, 8) ??
              "—"}
          </span>{" "}
          → {" "}
          <span className="font-medium">
            {incident.new_vehicle_registration ??
              incident.new_vehicle_id?.slice(0, 8) ??
              "—"}
          </span>
        </div>
      )}
      {(incident.old_driver_id !== null ||
        incident.new_driver_id !== null) && (
        <div className="text-xs text-muted-foreground">
          <span className="uppercase tracking-wide">Driver:</span>{" "}
          <span className="font-medium">
            {incident.old_driver_name ??
              incident.old_driver_id?.slice(0, 8) ??
              "—"}
          </span>{" "}
          → {" "}
          <span className="font-medium">
            {incident.new_driver_name ??
              incident.new_driver_id?.slice(0, 8) ??
              "—"}
          </span>
        </div>
      )}
      {incident.resolved_by_staff_name !== null && (
        <div className="text-xs text-muted-foreground">
          <span className="uppercase tracking-wide">Resolved by:</span>{" "}
          <span className="font-medium">{incident.resolved_by_staff_name}</span>
        </div>
      )}
    </li>
  );
}

function HistoryRow({
  entry,
}: {
  entry: AdminTripStatusHistoryRow;
}): React.JSX.Element {
  return (
    <li className="border-l-2 pl-3 py-1 ml-1 text-sm">
      <div>
        <span className="text-muted-foreground capitalize">
          {entry.old_status?.replace(/_/g, " ") ?? "—"}
        </span>
        {" → "}
        <span className="font-medium capitalize">
          {entry.new_status.replace(/_/g, " ")}
        </span>
      </div>
      <div className="text-xs text-muted-foreground">
        {formatDateTime(entry.changed_at)}
        {entry.change_source !== null && (
          <> · {entry.change_source}</>
        )}
      </div>
      {entry.notes !== null && (
        <div className="text-xs text-muted-foreground italic">
          {entry.notes}
        </div>
      )}
    </li>
  );
}

export function TripDetailPage(): React.JSX.Element {
  const { tripId } = useParams<{ tripId: string }>();
  const [accessToken] = useAccessToken();
  const [trip, setTrip] = useState<AdminTripDetail | null>(null);
  const [timeline, setTimeline] = useState<AdminTripTimelineResponse | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrip = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (!accessToken || !tripId) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await tripsAdminApi.detail(accessToken, tripId, signal);
        setTrip(res);
        // Fire-and-forget timeline fetch; failures don't break the page.
        void tripsAdminApi
          .timeline(accessToken, tripId, signal)
          .then(setTimeline)
          .catch(() => { /* timeline panel renders nothing */ });
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        if (e instanceof ApiError) {
          setError(e.problem.detail);
        } else {
          setError("Unexpected error. Try again.");
        }
      } finally {
        setLoading(false);
      }
    },
    [accessToken, tripId],
  );

  useEffect(() => {
    const c = new AbortController();
    void fetchTrip(c.signal);
    return () => {
      c.abort();
    };
  }, [fetchTrip]);

  return (
    <div className="space-y-4">
      <PageHeader
        title={trip !== null ? `Trip · ${laneLabel(trip)}` : "Trip detail"}
        description={
          <Link
            to="/trips/live"
            className="inline-flex items-center text-sm hover:underline"
          >
            <ArrowLeft className="mr-1 h-3 w-3" />
            Back to live trips
          </Link>
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading || trip === null ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-32 w-2/3" />
        </div>
      ) : (
        <>
          {/* 1. Header card */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    {laneLabel(trip)}
                  </CardTitle>
                  <CardDescription>
                    Trip{" "}
                    <span
                      className="font-mono text-xs"
                      title={trip.id}
                    >
                      {trip.id.slice(0, 8)}…
                    </span>
                    {trip.estimated_distance_km !== null && (
                      <> · {trip.estimated_distance_km} km</>
                    )}
                    {trip.payment_method !== null && (
                      <> · {trip.payment_method}</>
                    )}
                  </CardDescription>
                </div>
                <span
                  className={cn(
                    "inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                    statusPillClass(trip.status),
                  )}
                >
                  {trip.status.replace(/_/g, " ")}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Field
                  label="Pickup"
                  value={trip.pickup_address ?? trip.pickup_district ?? "—"}
                />
                <Field
                  label="Drop"
                  value={trip.drop_address ?? trip.drop_district ?? "—"}
                />
                <Field label="Material" value={trip.material_category ?? "—"} />
                <Field
                  label="Weight"
                  value={
                    trip.weight_kg !== null ? `${trip.weight_kg} kg` : "—"
                  }
                />
                <Field label="Created" value={formatDateTime(trip.created_at)} />
                <Field label="Accepted" value={formatDateTime(trip.accepted_at)} />
                <Field
                  label="Picked up"
                  value={formatDateTime(trip.actual_pickup_at)}
                />
                <Field
                  label="Completed"
                  value={formatDateTime(trip.actual_completion_at)}
                />
                <Field
                  label="Replacement"
                  value={String(trip.replacement_count)}
                />
                <Field
                  label="Substitution"
                  value={String(trip.substitution_count)}
                />
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">
                    Load
                  </div>
                  <Link
                    to={`/loads/${trip.load_id}`}
                    className="text-sm font-medium underline hover:text-primary"
                  >
                    {trip.load_id.slice(0, 8)}…
                  </Link>
                </div>
                {trip.gps_recorded_at !== null && (
                  <Field
                    label="Last ping"
                    value={formatDateTime(trip.gps_recorded_at)}
                  />
                )}
                {trip.cancelled_at !== null && (
                  <Field
                    label="Cancelled"
                    value={formatDateTime(trip.cancelled_at)}
                  />
                )}
              </div>
              {trip.cancellation_reason !== null && (
                <Alert className="mt-4" variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Cancelled: {trip.cancellation_reason}
                  </AlertDescription>
                </Alert>
              )}
              {trip.released_at !== null && (
                <Alert className="mt-4" variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Driver released:{" "}
                    {trip.released_by_driver_reason ?? "(no reason)"}
                    {trip.released_by_driver_notes !== null && (
                      <> · {trip.released_by_driver_notes}</>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* 2. Parties + vehicle */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" /> Parties &amp; vehicle
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <PartyChip label="Shipper" party={trip.shipper} />
                <PartyChip label="Driver" party={trip.driver} />
                <PartyChip label="Owner" party={trip.owner} />
                <VehicleChip vehicle={trip.vehicle} />
              </div>
            </CardContent>
          </Card>

          {/* 3. Money */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" /> Money
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Field label="Trip value" value={`₹${trip.trip_value}`} />
                <Field
                  label="Platform commission"
                  value={`₹${trip.platform_commission}`}
                />
                <Field
                  label="Driver rate"
                  value={
                    trip.driver_commission_rate_applied !== null
                      ? `${(Number(trip.driver_commission_rate_applied) * 100).toFixed(2)} %`
                      : "—"
                  }
                />
                <Field
                  label="Shipper rate"
                  value={
                    trip.shipper_commission_rate_applied !== null
                      ? `${(Number(trip.shipper_commission_rate_applied) * 100).toFixed(2)} %`
                      : "—"
                  }
                />
                <Field
                  label="Collection status"
                  value={trip.commission_collection_status}
                />
                <Field
                  label="Collected at"
                  value={formatDateTime(trip.commission_collected_at)}
                />
                {trip.cancellation_fee_inr !== null && (
                  <Field
                    label="Cancellation fee"
                    value={`₹${trip.cancellation_fee_inr}`}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* 3.5 Admin actions */}
          <TripActionsCard
            trip={trip}
            accessToken={accessToken}
            onActionComplete={() => void fetchTrip()}
          />

          {/* 4a. Stops */}
          <Card>
            <CardHeader>
              <CardTitle>Stops ({trip.stops.length})</CardTitle>
              <CardDescription>
                Stop-by-stop progress for multi-stop trips. Single-leg
                trips show no rows here — the pickup + drop columns
                above hold the same data.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trip.stops.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No stop progress logged yet.
                </div>
              ) : (
                <ul className="space-y-2">
                  {trip.stops.map((s) => (
                    <StopRow key={s.id} stop={s} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* 4b. Incidents */}
          <Card>
            <CardHeader>
              <CardTitle>Incidents ({trip.incidents.length})</CardTitle>
              <CardDescription>
                Vehicle swaps, driver substitutions, support-takeover
                events.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trip.incidents.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No incidents on this trip.
                </div>
              ) : (
                <ul className="space-y-2">
                  {trip.incidents.map((i) => (
                    <IncidentRow key={i.id} incident={i} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* 4c. Status history */}
          <Card>
            <CardHeader>
              <CardTitle>Status history ({trip.status_history.length})</CardTitle>
              <CardDescription>
                Every status transition logged in
                audit.trip_status_changes. Oldest first.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trip.status_history.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No status changes logged.
                </div>
              ) : (
                <ul className="space-y-1">
                  {trip.status_history.map((h, idx) => (
                    <HistoryRow key={`${h.changed_at}-${idx}`} entry={h} />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* 4d. Unified timeline — status + incidents + ledger + disputes */}
          {timeline !== null && (
            <Card>
              <CardHeader>
                <CardTitle>Unified timeline ({timeline.entries.length})</CardTitle>
                <CardDescription>
                  Status, incidents, ledger entries and disputes merged
                  in time order. The single narrative view for triage.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {timeline.entries.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    Nothing recorded yet.
                  </div>
                ) : (
                  <ul className="space-y-1">
                    {timeline.entries.map((entry, idx) => (
                      <TimelineRow
                        key={`${entry.at}-${entry.kind}-${idx}`}
                        entry={entry}
                      />
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function TimelineRow({
  entry,
}: {
  entry: AdminTripTimelineEntry;
}): React.JSX.Element {
  const tone = (() => {
    switch (entry.kind) {
      case "status_change":
        return "bg-info-subtle text-info-subtle-foreground";
      case "incident":
        return "bg-warning-subtle text-warning-subtle-foreground";
      case "ledger":
        return "bg-success-subtle text-success-subtle-foreground";
      case "dispute":
        return "bg-danger-subtle text-danger-subtle-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  })();
  return (
    <li className="flex items-start gap-2 py-1 text-sm">
      <span
        className={cn(
          "shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide font-medium",
          tone,
        )}
      >
        {entry.kind.replace("_", " ")}
      </span>
      <div className="min-w-0 flex-1">
        <div className="break-words">{entry.summary}</div>
        <div className="text-xs text-muted-foreground">
          {new Date(entry.at).toLocaleString()}
          {entry.actor_user_id !== null && (
            <span className="ml-1 font-mono">
              · by {entry.actor_user_id.slice(0, 8)}
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm font-medium break-words">{value}</div>
    </div>
  );
}

// Local hook so the page doesn't depend on a specific provider signature.
import { useAuth } from "@/lib/auth";
function useAccessToken(): [string | null] {
  const { accessToken } = useAuth();
  return [accessToken];
}

// =============================================================================
// Admin actions card
// =============================================================================
// Three operator-facing actions backed by existing /trips/{id}/* endpoints
// (they whitelist admins via primary_role check). Buttons disable when the
// trip is in a terminal status; each opens an inline form pane so the
// admin doesn't lose context.
// =============================================================================

type ActionForm =
  | "cancel"
  | "force_cancel"
  | "replace_vehicle"
  | "substitute_driver"
  | null;

const TERMINAL_STATUSES = new Set(["completed", "cancelled", "disputed"]);

function TripActionsCard({
  trip,
  accessToken,
  onActionComplete,
}: {
  trip: AdminTripDetail;
  accessToken: string | null;
  onActionComplete: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const [openForm, setOpenForm] = useState<ActionForm>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // Cancel form
  const [cancelReason, setCancelReason] = useState<string>("");
  const [cancelRepost, setCancelRepost] = useState<boolean>(false);

  // Force-cancel form (zero-fee admin override)
  const [forceCancelReason, setForceCancelReason] = useState<string>("");

  // Replace-vehicle form
  const [newVehicleId, setNewVehicleId] = useState<string>("");
  const [replaceDriverId, setReplaceDriverId] = useState<string>("");
  const [replaceReason, setReplaceReason] = useState<string>("");

  // Substitute-driver form
  const [newDriverId, setNewDriverId] = useState<string>("");
  const [substituteReason, setSubstituteReason] = useState<string>("");

  const terminal = TERMINAL_STATUSES.has(trip.status);

  const resetForms = (): void => {
    setCancelReason("");
    setCancelRepost(false);
    setForceCancelReason("");
    setNewVehicleId("");
    setReplaceDriverId("");
    setReplaceReason("");
    setNewDriverId("");
    setSubstituteReason("");
    setError(null);
  };

  const handleOpen = (form: Exclude<ActionForm, null>): void => {
    resetForms();
    setOpenForm(form);
  };

  const handleClose = (): void => {
    setOpenForm(null);
    resetForms();
  };

  const onSuccess = (msg: string): void => {
    setFlash(msg);
    toast.success(msg);
    setOpenForm(null);
    resetForms();
    onActionComplete();
  };

  const onFailure = (e: unknown, fallback: string): void => {
    if (e instanceof ApiError) {
      setError(e.problem.detail);
      toast.error(e.problem.detail);
    } else {
      setError(fallback);
      toast.error(fallback);
    }
  };

  const doCancel = async (): Promise<void> => {
    if (!accessToken) {return;}
    setBusy(true);
    setError(null);
    try {
      await tripsAdminApi.cancel(accessToken, trip.id, {
        ...(cancelReason.trim().length > 0
          ? { cancellation_reason: cancelReason.trim() }
          : {}),
        repost: cancelRepost,
      });
      onSuccess("Trip cancelled.");
    } catch (e) {
      onFailure(e, "Cancel failed.");
    } finally {
      setBusy(false);
    }
  };

  const doForceCancel = async (): Promise<void> => {
    if (!accessToken) {return;}
    if (forceCancelReason.trim().length < 4) {
      setError("Force-cancel reason must be at least 4 characters.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await tripsAdminApi.forceCancel(
        accessToken, trip.id, forceCancelReason.trim(),
      );
      onSuccess("Trip force-cancelled with zero fee.");
    } catch (e) {
      onFailure(e, "Force-cancel failed.");
    } finally {
      setBusy(false);
    }
  };

  const doReplaceVehicle = async (): Promise<void> => {
    if (!accessToken) {return;}
    if (newVehicleId.trim().length === 0) {
      setError("Enter the replacement vehicle's UUID.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await tripsAdminApi.replaceVehicle(accessToken, trip.id, {
        new_vehicle_id: newVehicleId.trim(),
        ...(replaceDriverId.trim().length > 0
          ? { new_driver_id: replaceDriverId.trim() }
          : {}),
        ...(replaceReason.trim().length > 0
          ? { reason: replaceReason.trim() }
          : {}),
      });
      onSuccess("Vehicle replaced.");
    } catch (e) {
      onFailure(e, "Replace vehicle failed.");
    } finally {
      setBusy(false);
    }
  };

  const doSubstituteDriver = async (): Promise<void> => {
    if (!accessToken) {return;}
    if (newDriverId.trim().length === 0) {
      setError("Enter the replacement driver's UUID.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await tripsAdminApi.substituteDriver(accessToken, trip.id, {
        new_driver_id: newDriverId.trim(),
        ...(substituteReason.trim().length > 0
          ? { reason: substituteReason.trim() }
          : {}),
      });
      onSuccess("Driver substituted.");
    } catch (e) {
      onFailure(e, "Substitute driver failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="trip-actions-card">
      <CardHeader>
        <CardTitle>Admin actions</CardTitle>
        <CardDescription>
          {terminal
            ? `Trip is in terminal status "${trip.status}". Actions are disabled.`
            : "Cancel the trip, swap the vehicle, or substitute the driver. Each writes an audit row."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {flash !== null && (
          <Alert className="mb-3">
            <AlertDescription>{flash}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={terminal}
            onClick={() => { handleOpen("replace_vehicle"); }}
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Replace vehicle
          </Button>
          <Button
            variant="outline"
            disabled={terminal}
            onClick={() => { handleOpen("substitute_driver"); }}
          >
            <UserCog className="mr-1 h-4 w-4" />
            Substitute driver
          </Button>
          <Button
            variant="destructive"
            disabled={terminal}
            onClick={() => { handleOpen("cancel"); }}
          >
            <Ban className="mr-1 h-4 w-4" />
            Cancel trip
          </Button>
          <Button
            variant="destructive"
            disabled={terminal}
            onClick={() => { handleOpen("force_cancel"); }}
            title="Zero-fee cancel — for dispute outcomes or stuck-state recovery."
          >
            <Ban className="mr-1 h-4 w-4" />
            Force-cancel (no fee)
          </Button>
        </div>

        {openForm !== null && (
          <div className="mt-4 rounded-md border p-4 space-y-3">
            {openForm === "cancel" && (
              <>
                <div>
                  <Label htmlFor="cancel-reason">Cancellation reason</Label>
                  <Textarea
                    id="cancel-reason"
                    value={cancelReason}
                    onChange={(e) => { setCancelReason(e.target.value); }}
                    rows={2}
                    maxLength={500}
                    placeholder="Optional — visible on the audit row"
                    disabled={busy}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={cancelRepost}
                    onChange={(e) => { setCancelRepost(e.target.checked); }}
                    disabled={busy}
                  />
                  Repost the load (clones load + reopens bids + sets shipper
                  mediation flag)
                </label>
              </>
            )}

            {openForm === "force_cancel" && (
              <>
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Zero-fee admin override. Use for dispute outcomes or
                    when a trip is stuck in a non-terminal state. Skips the
                    standard 5% cancellation fee.
                  </AlertDescription>
                </Alert>
                <div>
                  <Label htmlFor="force-cancel-reason">
                    Reason (required, ≥ 4 chars)
                  </Label>
                  <Textarea
                    id="force-cancel-reason"
                    value={forceCancelReason}
                    onChange={(e) => { setForceCancelReason(e.target.value); }}
                    rows={2}
                    maxLength={500}
                    placeholder="Dispute panel ruled X; refund both parties"
                    disabled={busy}
                  />
                </div>
              </>
            )}

            {openForm === "replace_vehicle" && (
              <>
                <div>
                  <Label htmlFor="replace-vehicle-id">
                    Replacement vehicle id
                  </Label>
                  <Input
                    id="replace-vehicle-id"
                    value={newVehicleId}
                    onChange={(e) => { setNewVehicleId(e.target.value); }}
                    placeholder="UUID of the replacement vehicle"
                    disabled={busy}
                  />
                </div>
                <div>
                  <Label htmlFor="replace-driver-id">
                    New driver id (optional)
                  </Label>
                  <Input
                    id="replace-driver-id"
                    value={replaceDriverId}
                    onChange={(e) => { setReplaceDriverId(e.target.value); }}
                    placeholder="Only if the current driver isn't paired to the new vehicle"
                    disabled={busy}
                  />
                </div>
                <div>
                  <Label htmlFor="replace-reason">Reason</Label>
                  <Input
                    id="replace-reason"
                    value={replaceReason}
                    onChange={(e) => { setReplaceReason(e.target.value); }}
                    placeholder="Optional"
                    disabled={busy}
                  />
                </div>
              </>
            )}

            {openForm === "substitute_driver" && (
              <>
                <div>
                  <Label htmlFor="substitute-driver-id">
                    Replacement driver id
                  </Label>
                  <Input
                    id="substitute-driver-id"
                    value={newDriverId}
                    onChange={(e) => { setNewDriverId(e.target.value); }}
                    placeholder="UUID of the new driver (must be paired to the current vehicle)"
                    disabled={busy}
                  />
                </div>
                <div>
                  <Label htmlFor="substitute-reason">Reason</Label>
                  <Input
                    id="substitute-reason"
                    value={substituteReason}
                    onChange={(e) => { setSubstituteReason(e.target.value); }}
                    placeholder="Optional"
                    disabled={busy}
                  />
                </div>
              </>
            )}

            {error !== null && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={handleClose} disabled={busy}>
                Cancel
              </Button>
              {openForm === "cancel" && (
                <Button
                  variant="destructive"
                  onClick={() => void doCancel()}
                  disabled={busy}
                >
                  {busy ? "Cancelling…" : "Cancel trip"}
                </Button>
              )}
              {openForm === "force_cancel" && (
                <Button
                  variant="destructive"
                  onClick={() => void doForceCancel()}
                  disabled={busy || forceCancelReason.trim().length < 4}
                >
                  {busy ? "Force-cancelling…" : "Force-cancel (no fee)"}
                </Button>
              )}
              {openForm === "replace_vehicle" && (
                <Button
                  onClick={() => void doReplaceVehicle()}
                  disabled={busy || newVehicleId.trim().length === 0}
                >
                  {busy ? "Replacing…" : "Replace vehicle"}
                </Button>
              )}
              {openForm === "substitute_driver" && (
                <Button
                  onClick={() => void doSubstituteDriver()}
                  disabled={busy || newDriverId.trim().length === 0}
                >
                  {busy ? "Substituting…" : "Substitute driver"}
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
